/**
 * A class for managing a lightbox feature within a product gallery. Handles opening, closing,
 * navigation, and rendering of images in a full-screen modal window.
 */
export class ProductGalleryLightbox {

	/**
	 * Initializes a product gallery instance, allowing for the setup of event listeners and lightbox functionality.
	 *
	 * @param {string} [selector='.product-gallery'] - A CSS selector string used to identify galleries on the page. Defaults to '.product-gallery'.
	 * @return {void} Does not return any value. Constructs the gallery instance or exits if no galleries are found.
	 */
	constructor( selector = '.product-gallery' ) {

		this.galleries = Array.from( document.querySelectorAll( selector ) );
		if ( ! this.galleries.length ) {

			return;

		}

		this.lightbox = null;
		this.items = [];
		this.index = 0;

		this.handleKeydown = this.handleKeydown.bind( this );

		this.galleries.forEach( ( gallery ) => {

			gallery.addEventListener( 'click', ( event ) => this.onGalleryClick( event, gallery ) );

		} );

	}

	/**
	 * Handles the click event on a gallery element, identifies the clicked gallery item,
	 * collects relevant data from all gallery items, and opens the gallery at the selected item.
	 *
	 * @param {Event} event The click event triggered on the gallery.
	 * @param {Element} gallery The gallery element containing the clickable items.
	 * @return {void} Does not return a value. The function processes the event and updates the state of the gallery.
	 */
	onGalleryClick( event, gallery ) {

		const item = event.target.closest( '.product-gallery__item' );
		if ( ! item || ! gallery.contains( item ) ) {

			return;

		}

		event.preventDefault();

		const nodes = Array.from( gallery.querySelectorAll( '.product-gallery__item' ) );
		this.items = nodes.map( ( node ) => {

			const img = node.querySelector( 'img' );
			return {
				src: node.getAttribute( 'href' ),
				alt: img ? img.getAttribute( 'alt' ) || '' : '',
			};

		} );

		this.index = Math.max( 0, nodes.indexOf( item ) );
		this.open();

	}

	/**
	 * Builds and initializes the lightbox component for displaying images.
	 * This method creates all necessary DOM elements for the lightbox
	 * and attaches event listeners for user interaction, such as navigating
	 * through images or closing the lightbox.
	 *
	 * @return {void} This method does not return a value.
	 */
	buildLightbox() {

		if ( this.lightbox ) {

			return;

		}

		const wrapper = document.createElement( 'div' );
		wrapper.className = 'product-lightbox';
		wrapper.setAttribute( 'role', 'dialog' );
		wrapper.setAttribute( 'aria-modal', 'true' );
		wrapper.setAttribute( 'aria-hidden', 'true' );

		wrapper.innerHTML = `
			<button class="product-lightbox__close" type="button" aria-label="Close gallery"></button>
			<button class="product-lightbox__nav product-lightbox__prev" type="button" aria-label="Previous image"></button>
			<img class="product-lightbox__image" alt="" />
			<button class="product-lightbox__nav product-lightbox__next" type="button" aria-label="Next image"></button>
		`;

		document.body.appendChild( wrapper );
		this.lightbox = wrapper;

		this.lightbox.addEventListener( 'click', ( event ) => {

			if ( event.target === this.lightbox ) {

				this.close();

			}

		} );

		this.lightbox.querySelector( '.product-lightbox__close' )
			.addEventListener( 'click', () => this.close() );
		this.lightbox.querySelector( '.product-lightbox__prev' )
			.addEventListener( 'click', () => this.prev() );
		this.lightbox.querySelector( '.product-lightbox__next' )
			.addEventListener( 'click', () => this.next() );

	}

	/**
	 * Opens the lightbox for viewing items. Performs necessary setup,
	 * updates the DOM to indicate the lightbox is open, and attaches event
	 * listeners for interactivity.
	 *
	 * @return {void} This method does not return any value.
	 */
	open() {

		if ( ! this.items.length ) {

			return;

		}

		this.buildLightbox();
		this.lightbox.classList.add( 'is-open' );
		this.lightbox.setAttribute( 'aria-hidden', 'false' );
		document.body.classList.add( 'is-product-lightbox-open' );
		this.render();
		document.addEventListener( 'keydown', this.handleKeydown );

	}

	/**
	 * Closes the lightbox by removing relevant classes and event listeners.
	 *
	 * @return {void} No return value.
	 */
	close() {

		if ( ! this.lightbox ) {

			return;

		}

		this.lightbox.classList.remove( 'is-open' );
		this.lightbox.setAttribute( 'aria-hidden', 'true' );
		document.body.classList.remove( 'is-product-lightbox-open' );
		document.removeEventListener( 'keydown', this.handleKeydown );

	}

	/**
	 * Updates the lightbox display with the current item's image and manages navigation controls.
	 *
	 * @return {void} This method does not return a value.
	 */
	render() {

		const item = this.items[ this.index ];
		const image = this.lightbox.querySelector( '.product-lightbox__image' );
		image.src = item.src;
		image.alt = item.alt;

		const prev = this.lightbox.querySelector( '.product-lightbox__prev' );
		const next = this.lightbox.querySelector( '.product-lightbox__next' );
		const hasNav = this.items.length > 1;
		prev.disabled = ! hasNav;
		next.disabled = ! hasNav;

	}

	/**
	 * Advances to the next item in the collection.
	 * If the collection contains fewer than two items, no action is taken.
	 * Updates the current index to the next position, looping back to the beginning if necessary, and triggers a render.
	 *
	 * @return {void} No return value.
	 */
	next() {

		if ( this.items.length < 2 ) {

			return;

		}

		this.index = ( this.index + 1 ) % this.items.length;
		this.render();

	}

	/**
	 * Moves to the previous item in the collection.
	 * Updates the current index and triggers the rendering of the updated state.
	 *
	 * @return {void} Does not return a value.
	 */
	prev() {

		if ( this.items.length < 2 ) {

			return;

		}

		this.index = ( this.index - 1 + this.items.length ) % this.items.length;
		this.render();

	}

	/**
	 * Handles the `keydown` event to manage lightbox navigation and closing functionality.
	 *
	 * @param {KeyboardEvent} event - The keyboard event triggered by user interaction.
	 * @return {void} Does not return a value.
	 */
	handleKeydown( event ) {

		if ( ! this.lightbox || ! this.lightbox.classList.contains( 'is-open' ) ) {

			return;

		}

		if ( event.key === 'Escape' ) {

			this.close();
			return;

		}

		if ( event.key === 'ArrowRight' ) {

			this.next();
			return;

		}

		if ( event.key === 'ArrowLeft' ) {

			this.prev();

		}

	}

}
